<template>
  <div
    ref="container"
    class="release-highlight-themed-svg"
    role="img"
    :aria-label="label"
    data-qa="release-highlight-themed-svg"
  />
</template>

<script setup lang="ts">
import {
  onMounted,
  useTemplateRef,
  watch,
} from 'vue';

const props = defineProps<{
  /** URL of an SVG drawn with the CSS variables of the theme. */
  url: string,
  label: string,
}>();

const containerRef = useTemplateRef<HTMLDivElement>('container');
let latestRequestedUrl: string | undefined;

/** The files are part of the release, but nothing that runs code is placed into the page. */
function removeScripts(svg: Element) {
  svg.querySelectorAll('script, foreignObject').forEach((element) => element.remove());
  [
    svg,
    ...svg.querySelectorAll('*'),
  ].forEach((element) => {
    Array.from(element.attributes)
      .filter((attribute) => attribute.name.toLowerCase().startsWith('on'))
      .forEach((attribute) => element.removeAttribute(attribute.name));
  });
}

async function placeSvg(url: string) {
  latestRequestedUrl = url;
  const response = await fetch(url);
  if (!response.ok || url !== latestRequestedUrl || !containerRef.value) {
    return;
  }
  const svgDocument = new DOMParser().parseFromString(await response.text(), 'image/svg+xml');
  const svg = svgDocument.documentElement;
  if (svg.nodeName !== 'svg') {
    return;
  }
  removeScripts(svg);
  svg.setAttribute('aria-hidden', 'true');
  containerRef.value.replaceChildren(document.importNode(svg, true));
}

onMounted(() => {
  void placeSvg(props.url);
});
watch(() => props.url, (url) => {
  void placeSvg(url);
});
</script>

<style scoped lang="scss">
.release-highlight-themed-svg :deep(svg) {
  display: block;
  width: 100%;
  height: 100%;
}
</style>
